import { api } from './klient';
import type { Fil } from './filer';
import type { Fremdrift, Sammendrag, Sporsmal, Sprak, Svarverdi } from './reise';

// Typene speiler API-et for arbeidsbordet (docs/beslutninger/0020 og 0026).

export interface Ansatt {
  epost: string;
}

export interface Reiselinje {
  id: string;
  kundenavn: string;
  sprak: Sprak;
  modus: string;
  tilstand: 'aktiv' | 'bekreftet' | 'trukket_tilbake';
  opprettet: string;
  opprettetAv: string;
  sistAktiv: string | null;
}

export interface NyReise {
  kundenavn: string;
  sprak: Sprak;
  modus: 'alene' | 'med_konsulent';
  kontaktNavn: string;
  kontaktEpost: string;
  salgReferanse?: string;
  /** Send lenken til kontaktpersonen på e-post (T112). */
  sendInvitasjon?: boolean;
}

/** Invitasjonen som er sendt på e-post (T112). */
export interface Invitasjon {
  lenke: string;
  sendt: string;
  til: string;
}

export interface Hendelse {
  id: number;
  sporsmalId: string;
  instans: string;
  modulId: string | null;
  sporsmalTekst: string | null;
  beslutningId: string;
  verdi: Svarverdi;
  kundensOrd: string | null;
  sikkerhet: string;
  kilde: string;
  status: string;
  vetIkke: boolean;
  av: string;
  begrunnelse: string | null;
  tidspunkt: string;
}

export interface Flagg {
  id: number;
  type: string;
  tekst: string;
  sporsmalId: string | null;
  status: string;
  notat: string | null;
  teamworkOppgave: string | null;
  opprettet: string;
  endret: string | null;
  endretAv: string | null;
}

export interface SammendragInfo {
  versjon: number;
  sprak: string;
  fingeravtrykk: string;
  opprettet: string;
  bekreftetAv: string | null;
  bekreftet: string | null;
  status: string | null;
  godkjentAv: string | null;
  godkjent: string | null;
}

export interface Tidsforing {
  id: number;
  steg: string;
  person: string;
  aktivitet: string;
  minutter: number;
  tidspunkt: string;
}

export interface Tidtaker {
  person: string;
  steg: string;
  aktivitet: string;
  startet: string;
}

export interface ReiseDetaljer {
  reise: Reiselinje & { innholdVersjonId: number; salgReferanse: string | null; paminnelserAv?: boolean };
  deltakere: { id: string; navn: string; epost: string; rolle: string | null; sprak: string; moduler: string[]; erEier: boolean; sistAktiv: string | null; invitasjonSendt?: string | null }[];
  fremdrift: Fremdrift;
  svar: Hendelse[];
  flagg: Flagg[];
  sammendrag: SammendragInfo | null;
  tid: { perSteg: Record<string, number>; totalt: number; foringer: Tidsforing[]; pagaende: Tidtaker[] };
  rettelser: number;
  motsigelser: { modulId: string; id: string; tekst: string; sporsmalIder: string[] }[];
  ubesvarte: { sporsmalId: string; instans: string; modulId: string; tekst: string; profil: string | null }[];
}

export interface Rettelse {
  sporsmalId: string;
  instans: string;
  verdi: Svarverdi | undefined;
  kundensOrd: string | null | undefined;
  sikkerhet: string;
  begrunnelse: string;
}

const reise = (id: string) => `/api/konsoll/reiser/${encodeURIComponent(id)}`;

export interface OverleveringVersjon {
  versjon: number;
  formatVersjon: string;
  laastTidspunkt: string;
  laastAv: string;
  fingeravtrykk: string;
  status: string;
  statusEndret: string | null;
  statusEndretAv: string | null;
  filer: number;
  /** Statusene en ansatt kan sette nå. */
  neste: string[];
}

export interface OverleveringOversikt {
  versjoner: OverleveringVersjon[];
  /** En godkjent versjon som venter på å bli låst. */
  venter: number | null;
}

export const konsollApi = {
  meg: () => api<Ansatt>('/api/konsoll/meg'),
  loggInn: (epost: string, passord: string) => api<Ansatt>('/api/konsoll/logginn', 'POST', { epost, passord }),
  loggUt: () => api<void>('/api/konsoll/loggut', 'POST'),
  reiser: () => api<Reiselinje[]>('/api/konsoll/reiser'),
  opprett: (ny: NyReise) => api<{ reiseId: string; lenke: string; invitasjonSendt: string | null }>('/api/konsoll/reiser', 'POST', ny),
  /** Ny lenke til kontaktpersonen, sendt på e-post. Den gamle lenken slutter å virke. */
  sendInvitasjon: (id: string) => api<Invitasjon>(`${reise(id)}/invitasjon`, 'POST'),
  /** Slår påminnelsene på e-post av eller på (T113). */
  settPaminnelser: (id: string, av: boolean) => api<void>(`${reise(id)}/paminnelser`, 'PUT', { av }),
  /** Om den innloggede ansatte får varsler på e-post (T114). */
  varsler: () => api<{ pa: boolean }>('/api/konsoll/meg/varsler'),
  settVarsler: (pa: boolean) => api<{ pa: boolean }>('/api/konsoll/meg/varsler', 'PUT', { pa }),
  trekkTilbake: (id: string) => api<void>(`${reise(id)}/trekk-tilbake`, 'POST'),
  detaljer: (id: string) => api<ReiseDetaljer>(reise(id)),
  sporsmal: (id: string, sporsmalId: string, instans: string) =>
    api<Sporsmal>(`${reise(id)}/sporsmal/${encodeURIComponent(sporsmalId)}?instans=${encodeURIComponent(instans)}`),
  rett: (id: string, rettelse: Rettelse) => api<unknown>(`${reise(id)}/svar`, 'POST', rettelse),
  oppdaterFlagg: (id: string, flaggId: number, endring: { status?: string; notat?: string }) =>
    api<Flagg>(`${reise(id)}/flagg/${flaggId}`, 'PATCH', endring),
  startTid: (id: string, steg: string, aktivitet: string) => api<Tidtaker>(`${reise(id)}/tid/start`, 'POST', { steg, aktivitet }),
  stoppTid: (id: string) => api<Tidsforing>(`${reise(id)}/tid/stopp`, 'POST'),
  forTid: (id: string, steg: string, aktivitet: string, minutter: number) =>
    api<Tidsforing>(`${reise(id)}/tid`, 'POST', { steg, aktivitet, minutter }),
  sammendrag: (id: string) => api<{ versjon: SammendragInfo; dokument: Sammendrag }>(`${reise(id)}/sammendrag`),
  godkjenn: (id: string) => api<SammendragInfo>(`${reise(id)}/sammendrag/godkjenn`, 'POST'),
  overlevering: (id: string) => api<OverleveringOversikt>(`${reise(id)}/overlevering`),
  dokumentasjon: (id: string) => api<Fil[]>(`${reise(id)}/dokumentasjon`),
  /** Filen lastes ned med en vanlig lenke, så nettleseren lagrer den. */
  filAdresse: (id: string, filId: string) => `${reise(id)}/filer/${encodeURIComponent(filId)}/innhold`,
  settOverleveringsstatus: (id: string, versjon: number, status: string) =>
    api<OverleveringOversikt>(`${reise(id)}/overlevering/${versjon}`, 'PATCH', { status }),
};

export const STEG = ['behovsmote', 'kundereise', 'gjennomgang', 'losningsforslag', 'bygging', 'demo', 'opplaering', 'oppfolging', 'annet'];
export const FLAGGSTATUSER = ['registrert', 'under_arbeid', 'lost', 'ikke_relevant'];
